import React, { FunctionComponent, useCallback, useEffect, useMemo } from 'react';

import { Brush } from '@yandex-360/iconostasis/dist/icons/Brush';
import { Camera } from '@yandex-360/iconostasis/dist/icons/Camera';
import { Diagram } from '@yandex-360/iconostasis/dist/icons/Diagram';
import { Faq } from '@yandex-360/iconostasis/dist/icons/Faq';
import { Image } from '@yandex-360/iconostasis/dist/icons/Image';
import { MicroOn } from '@yandex-360/iconostasis/dist/icons/MicroOn';
import { Profile } from '@yandex-360/iconostasis/dist/icons/Profile';
import { Shield } from '@yandex-360/iconostasis/dist/icons/Shield';
import { Star } from '@yandex-360/iconostasis/dist/icons/Star';
import { TelemostSettingsSectionKey as SettingsSectionKey } from '@yandex-360/telemost-sdk';
import { useSelector } from 'react-redux';

import { DEFAULT_MODAL_Z_INDEX } from '../../../constants';
import { supportsDeviceChange } from '../../../helpers/browser-capabilities';
import { i18n } from '../../../helpers/i18n';
import { useActions } from '../../../hooks';
import { selectIsTelemessengerExpEnabled } from '../../flags/selectors';
import { refreshDevicesListAction } from '../../settings/slice';
import { isMobileUa } from '../../uatraits';
import {
    changeActiveSectionAction,
    closeSettingsModal,
    selectActiveSectionKey,
    selectIsSettingsModalOpened,
} from '../slice';
import { SettingsProps } from '../types';
import { SubmodalsManager } from './components/submodals/manager';
import { SettingsV2Desktop } from './desktop';
import { useSettingsSectionsHiddenMap } from './hooks/use-settings-sections-hidden-map';
import { SettingsV2MenuItem, SettingsV2PreprocessedMenuItem } from './types';

const MENU_ITEMS: Record<SettingsSectionKey, SettingsV2PreprocessedMenuItem> = {
    [SettingsSectionKey.Account]: {
        id: SettingsSectionKey.Account,
        label: () => i18n('settings-v2', 'menu-item-account'),
        startSlot: <Profile variant="outline" size="sm" />,
    },
    [SettingsSectionKey.Theme]: {
        id: SettingsSectionKey.Theme,
        label: () => i18n('settings-v2', 'menu-item-theme'),
        startSlot: <Brush variant="outline" size="sm" />,
    },
    [SettingsSectionKey.Permissions]: {
        id: SettingsSectionKey.Permissions,
        label: () => i18n('settings-v2', 'menu-item-permissions'),
        startSlot: <Shield variant="outline" size="sm" />,
    },
    [SettingsSectionKey.VirtualBackground]: {
        id: SettingsSectionKey.VirtualBackground,
        label: () => i18n('settings-v2', 'menu-item-virtual-background'),
        startSlot: <Image variant="outline" size="sm" />,
    },
    [SettingsSectionKey.Sound]: {
        id: SettingsSectionKey.Sound,
        label: () => i18n('settings-v2', 'menu-item-sound'),
        startSlot: <MicroOn variant="outline" size="sm" />,
    },
    [SettingsSectionKey.Camera]: {
        id: SettingsSectionKey.Camera,
        label: () => i18n('settings-v2', 'menu-item-video'),
        startSlot: <Camera variant="outline" size="sm" />,
    },
    [SettingsSectionKey.Developers]: {
        id: SettingsSectionKey.Developers,
        label: () => i18n('settings-v2', 'menu-item-develop'),
        startSlot: <Star variant="outline" size="sm" />,
    },
    [SettingsSectionKey.Help]: {
        id: SettingsSectionKey.Help,
        label: () => i18n('settings-v2', 'menu-item-help'),
        startSlot: <Faq variant="outline" size="sm" />,
    },
    [SettingsSectionKey.Diagnostics]: {
        id: SettingsSectionKey.Diagnostics,
        label: () => i18n('settings-v2', 'menu-item-diagnostics'),
        startSlot: <Diagram variant="outline" size="sm" />,
    },
};

const getMenuItems = (isTelemessengerExpEnabled: boolean) => {
    const items = [];

    if (isTelemessengerExpEnabled) {
        items.push(MENU_ITEMS[SettingsSectionKey.Account], MENU_ITEMS[SettingsSectionKey.Theme]);
    } else {
        items.push(MENU_ITEMS[SettingsSectionKey.Theme], MENU_ITEMS[SettingsSectionKey.Account]);
    }

    items.push(
        MENU_ITEMS[SettingsSectionKey.Permissions],
        MENU_ITEMS[SettingsSectionKey.VirtualBackground],
        MENU_ITEMS[SettingsSectionKey.Sound],
        MENU_ITEMS[SettingsSectionKey.Camera],
        MENU_ITEMS[SettingsSectionKey.Developers],
        MENU_ITEMS[SettingsSectionKey.Help],
        MENU_ITEMS[SettingsSectionKey.Diagnostics]
    );

    return items;
};

const SUBMODAL_Z_INDEX = DEFAULT_MODAL_Z_INDEX + 1;

export const SettingsV2: FunctionComponent<SettingsProps> = (props) => {
    const isMobile = useSelector(isMobileUa);
    const actions = useActions({
        closeSettingsModal,
        refreshDevicesListAction,
        changeActiveSectionAction,
    });
    const isOpened = useSelector(selectIsSettingsModalOpened);
    const activeSectionKey = useSelector(selectActiveSectionKey);
    const isTelemessengerExpEnabled = useSelector(selectIsTelemessengerExpEnabled);
    const settingsSectionsHiddenMap = useSettingsSectionsHiddenMap();

    const availableMenuItems = useMemo(
        () =>
            getMenuItems(isTelemessengerExpEnabled).reduce((availableItems, { id, label, ...item }) => {
                if (settingsSectionsHiddenMap[id]) {
                    return availableItems;
                }

                availableItems.push({
                    ...item,
                    id,
                    label: label(),
                    testId: `menu-item-${id}`,
                });

                return availableItems;
            }, [] as SettingsV2MenuItem[]),
        [settingsSectionsHiddenMap, isTelemessengerExpEnabled]
    );

    const defaultSelectedItemKey = isMobile ? null : availableMenuItems[0].id;

    const handleClose = useCallback(() => {
        actions.closeSettingsModal();
    }, [actions]);

    const setActiveSectionKey = useCallback(
        (key: SettingsSectionKey | null) => {
            actions.changeActiveSectionAction(key);
        },
        [actions]
    );

    useEffect(() => {
        if (!activeSectionKey || settingsSectionsHiddenMap[activeSectionKey]) {
            setActiveSectionKey(defaultSelectedItemKey);
        }
    }, [activeSectionKey, defaultSelectedItemKey, setActiveSectionKey, settingsSectionsHiddenMap]);

    useEffect(() => {
        if (isOpened && supportsDeviceChange()) {
            actions.refreshDevicesListAction();
        }
    }, [isOpened, actions]);

    return (
        <>
            <SettingsV2Desktop
                {...props}
                zIndex={DEFAULT_MODAL_Z_INDEX}
                items={availableMenuItems}
                selectedItemId={activeSectionKey}
                onChangeSelectedItemId={setActiveSectionKey}
                visible={isOpened}
                onClose={handleClose}
            />
            <SubmodalsManager zIndex={SUBMODAL_Z_INDEX} />
        </>
    );
};
